// Typed fetch wrappers for every backend endpoint.
// All requests inject the auth token from localStorage and handle global errors.
//
// Cloudflare Pages port: in production, the SPA and the /api Functions are served
// from the same origin, so a relative "/api" base works. In dev you can either
// run `wrangler pages dev` (which wires the same-origin /api) and leave VITE_API_BASE
// unset, or run vite alone and point at a remote backend via VITE_API_BASE.

const TOKEN_KEY = "nedm.staff.token";

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE?.toString().replace(/\/$/, "") || "";

export function getToken(): string | null {
  return typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: any;
  query?: Record<string, string | number | boolean | undefined>;
  // For routes that don't require staff auth (intake page).
  publicRoute?: boolean;
  // For file uploads.
  formData?: FormData;
  // Override Authorization header (e.g., intake token).
  authOverride?: string;
}

function buildUrl(path: string): URL {
  // If an absolute API_BASE was provided (e.g. https://api.example.com), resolve
  // relative paths against it; otherwise resolve against window origin (same-origin).
  if (API_BASE && /^https?:\/\//i.test(API_BASE)) {
    // path is expected to start with "/api/..."; strip leading slash and join.
    return new URL(path.replace(/^\//, ""), API_BASE.endsWith("/") ? API_BASE : API_BASE + "/");
  }
  return new URL(path, window.location.origin);
}


/** The bare noexcusesdisputes.com / www root is the public sales page. */
export function isMarketingHost(): boolean {
  return typeof window !== "undefined" && /^(www\.)?noexcusesdisputes\.com$/i.test(window.location.hostname);
}
function isMarketingRoot(): boolean {
  return isMarketingHost() && window.location.pathname === "/";
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, publicRoute, formData, authOverride } = opts;

  const url = buildUrl(path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (!formData) headers["Content-Type"] = "application/json";

  if (authOverride) {
    headers["Authorization"] = `Bearer ${authOverride}`;
  } else if (!publicRoute) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: formData ? formData : body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
  } catch (e: any) {
    throw new ApiError(0, e?.message || "Network error", null);
  }

  let parsed: any = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!res.ok) {
    // Global 402 handling: the company's access is paused. Bounce to the
    // /suspended screen (unless we're already there) so staff see the paywall
    // notice instead of a broken page. Mirrors the 401 redirect below.
    if (
      res.status === 402 &&
      parsed && parsed.error === "company_suspended" &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/suspended")
    ) {
      window.location.href = "/suspended";
    }
    // Global 401 handling: drop token, force re-login.
    // EXCEPT on public client-facing pages (/intake/<token>, /filing/<token>) —
    // those are token-authenticated and the AuthProvider's boot-time /me check
    // is expected to 401 for anonymous clients. Don't bounce them to staff
    // sign-in; let the page render its own public form.
    if (res.status === 401 && !publicRoute) {
      setToken(null);
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/login") &&
        !window.location.pathname.startsWith("/forgot-password") &&
        !window.location.pathname.startsWith("/reset-password") &&
        !window.location.pathname.startsWith("/staff-invite/") &&
        !window.location.pathname.startsWith("/intake/") &&
        !window.location.pathname.startsWith("/start/") &&
        !window.location.pathname.startsWith("/filing/") &&
        !window.location.pathname.startsWith("/portal/") &&
        !window.location.pathname.startsWith("/join/") &&
        !window.location.pathname.startsWith("/affiliate/") &&
        !window.location.pathname.startsWith("/with/") &&
        !window.location.pathname.startsWith("/suspended") &&
        !window.location.pathname.startsWith("/pricing") &&
        !window.location.pathname.startsWith("/welcome") &&
        !window.location.pathname.startsWith("/start-checkout") &&
        !isMarketingRoot()
      ) {
        window.location.href = "/login";
      }
    }
    // Prefer the human-readable message over the raw error code so forms show
    // "Password must be at least 8 characters" instead of "validation_error".
    const msg = (parsed && (parsed.message || parsed.error)) || res.statusText;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

/* ---------- domain types ---------- */

export type ClientStatus = "pending" | "onboarding" | "active" | "suspended" | "needs_review" | "paused" | "completed" | "churned";
export type MonitoringProvider = "my_free_score_now" | "smart_credit" | "not_started";

export interface Client {
  /** 'lead' = prospect who has not signed; kept out of client pickers/counts. */
  record_type?: "client" | "lead";
  /** Days between automatic credit-report pulls; 0 = automatic pulls off. */
  auto_import_interval_days?: number;
  /** When the most recent report was pulled (any source). */
  last_report_at?: string | null;
  /** When the next automatic pull is due (null when off / no monitoring login). */
  next_auto_import_at?: string | null;
  id: string;
  created_at: string;
  status: ClientStatus;
  legal_name: string;
  preferred_name?: string | null;
  email: string;
  phone: string;
  dob_masked?: string | null; // "XX/XX/1985"
  ssn_masked?: string | null; // "XXX-XX-1234"
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  assigned_agent?: string | null;
  monthly_price?: number | null;
  monitoring: MonitoringProvider;
  score_start?: number | null;
  score_current?: number | null;
  negative_items?: number;
  deleted_items?: number;
  next_action?: string | null;
  notes?: string | null;
  /** Client→staff messages not yet opened by staff (list + detail). */
  unread_messages?: number;
  service_agreement_signed: boolean;
  /** Latest service-agreement record summary (see /api/clients/:id GET). */
  service_agreement?: ServiceAgreementSummary;
  /** CFPB portal login email (staff-only; password never returned). Used by
   *  the Online Dispute automation. */
  cfpb_login_email?: string | null;
  /** True when an encrypted CFPB password is saved (the secret itself is never sent). */
  has_cfpb_credentials?: boolean;
  monitoring_username?: string | null;
  /** Active drawn signature (see docs/CLIENT-SIGNATURE-SPEC.md); null when none on file. */
  signature?: SignatureMeta | null;
}

export interface ClientNote {
  id: string;
  author_user_id: string | null;
  author_name: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface ClientMessage {
  id: string;
  sender: "staff" | "client";
  author_user_id: string | null;
  author_name: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

export interface SignatureMeta {
  id: string;
  captured_at: string;
  source: "intake" | "portal" | "staff";
  width: number;
  height: number;
  sha256_short: string;
}

export type ServiceAgreementSummary =
  | { state: "not_sent" }
  | {
      state: "pending" | "signed" | "expired" | "revoked";
      id: string;
      template_version: string | null;
      sent_at: string | null;
      sent_to_email: string | null;
      signed_at: string | null;
      signed_by_name: string | null;
      sign_token_expires_at: string | null;
      revoked_at: string | null;
    };

export interface ClientListItem extends Client {}

export interface IntakeInvitation {
  id: string;
  client_id: string;
  token: string;
  delivery_channel: "sms" | "email" | "manual_link";
  delivered_to: string;
  expires_at: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  invite_url?: string;
}

export interface CreditReport {
  id: string;
  client_id: string;
  provider: "my_free_score_now" | "smart_credit" | "manual_upload";
  /** Only returned when fetching ONE report. The list endpoint returns
   *  body_bytes instead — the body is the whole credit file. */
  file_url?: string;
  /** Size of the stored report body, from the list endpoint. */
  body_bytes?: number | null;
  pulled_at: string;
  imported_at: string;
  parse_status: "pending" | "parsed" | "failed" | "needs_manual";
  parse_error?: string | null;
}

export interface DisputeRound {
  id: string;
  client_id: string;
  client_name?: string;
  round_number: number;
  // Backend canonical states.
  status: "drafting" | "ready" | "mailed" | "response_due" | "partially_complete" | "complete";
  bureau: "equifax" | "experian" | "transunion" | "all";
  items_count: number;
  due_date?: string | null;
  response_deadline?: string | null;
  notes?: string | null;
  mailed_at?: string | null;
  created_at: string;
}

export interface Letter {
  id: string;
  client_id: string;
  client_name?: string;
  dispute_round_id?: string | null;
  round_id?: string | null;
  kind: string;
  bureau?: string;
  recipient_bureau?: string;
  recipient?: string;
  // Backend uses these status values; older copy might still use draft/ready.
  // We keep the union broad so the StatusPill / table can render either.
  status:
    | "draft"
    | "drafting"
    | "quality_gate_pending"
    | "approved"
    | "ready"
    | "queued"
    | "queued_for_mail"
    | "mailed"
    | "delivered"
    | "responded";
  pdf_url?: string | null;
  body_text?: string | null;
  recipient_address?: string | null;
  // JSON-encoded analyzer + section metadata. For analyzer-generated letters this
  // carries { analysis_finding_ids, credit_report_id, source, fcra_basis,
  // manual_section?, manual_section_label? } so the UI can label each letter
  // (e.g. "§611 Hard Inquiries") without a new letter_kind enum value.
  quality_gate_notes?: string | null;
  created_at: string;
  // Set when the letter went out: through a mail job (mailed_source
  // 'mail_job', date from the carrier) or marked by hand ('manual').
  mailed_at?: string | null;
  delivered_at?: string | null;
  mailed_source?: "mail_job" | "manual" | null;
  mailed_note?: string | null;
}

export type MailProviderId = "lob" | "letterstream" | "manual" | "other";
export type MailJobStatus =
  | "draft"
  | "queued"
  | "sent"
  | "in_transit"
  | "delivered"
  | "returned"
  | "failed"
  | "cancelled"
  | "staff_mailed";

export interface MailProviderInfo {
  id: MailProviderId;
  name: string;
  supported_mail_types: ReadonlyArray<"first_class" | "certified">;
  configured: boolean;
  inflight: number;
}

export interface MailJob {
  id: string;
  letter_id: string;
  client_id?: string;
  client_name?: string;
  type?: string;
  letter_kind?: string | null;
  recipient_bureau?: string | null;
  // Backend doesn't include `destination` — it's a derived legacy field.
  destination?: string;
  provider: MailProviderId;
  provider_job_id?: string | null;
  // Backend canonical field. `delivery_class` is the legacy frontend alias.
  service_type?: "first_class" | "certified" | "certified_rr";
  delivery_class?: "first_class" | "certified" | "certified_rr";
  status: MailJobStatus;
  tracking_number?: string | null;
  return_receipt_url?: string | null;
  raw_provider_payload?: Record<string, unknown> | null;
  cost_cents?: number | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  created_at: string;
}

export type FilingKind = "cfpb_complaint" | "cfpb_collector_complaint" | "ftc_identity_theft" | "bureau_direct_upload";
export type FilingAgency = "cfpb" | "ftc" | "experian_direct" | "equifax_direct" | "transunion_direct";
export type FilingDraftStatus = "drafting" | "client_review" | "submitted" | "rejected";

export interface FilingBundleRef {
  bureau: "equifax" | "experian" | "transunion";
  round_id: string;
  round_number: number;
  filename: string;
  bundle_path: string;
}

export interface CfpbDraftContent {
  agency: string;
  wizard?: Record<string, unknown>;
  bureaus?: string[];
  company_entities?: Record<string, string>;
  narrative?: string;
  resolution?: string;
  attachments?: FilingBundleRef[];
  client_identity?: { name_as_on_report: string; ssn_last4_on_file: boolean; dob_on_file: boolean };
  cfpb_account_on_file?: boolean;
  blockers?: string[];
  checklist?: string[];
  generated_at?: string;
  [k: string]: unknown;
}

export interface Filing {
  id: string;
  client_id: string;
  client_name?: string;
  kind: FilingKind;
  agency: FilingAgency;
  draft_status: FilingDraftStatus;
  draft_content?: CfpbDraftContent;
  review_token?: string | null;
  submitted_at?: string | null;
  submitted_by_client_at?: string | null;
  confirmation_number?: string | null;
  operator_assisted_with?: boolean;
  cfpb_login_email?: string | null;
  created_at?: string;
}

export interface ComplianceDoc {
  id: string;
  client_id: string;
  client_name?: string;
  kind: "service_agreement" | "croa_disclosure" | "right_to_cancel";
  content_version: string;
  signed_at?: string | null;
}

export interface StaffUser {
  id: string;
  email: string;
  name: string;
  role: "owner" | "manager" | "analyst" | "viewer" | "agent" | "compliance";
  /** Set when the password used at last login no longer meets the strength policy. */
  password_update_required?: boolean;
  tenant_id?: string;
  is_super_admin?: boolean;
}

export interface DashboardStats {
  active_clients: number;
  onboarding: number;
  letters_ready: number;
  letters_mailable_today: number;
  disputes_pending: number;
  responses_due_this_week: number;
  monthly_billing_cents: number;
  failed_payments: number;
}

/* ---------- auth ---------- */

export const auth = {
  // Backend returns { ok, user } and sets an HttpOnly session cookie. No token in body.
  login: async (email: string, password: string) => {
    const r = await request<{ ok: true; user: StaffUser }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
      publicRoute: true,
    });
    return { user: r.user, token: "" };
  },
  me: async () => {
    const r = await request<{ ok: true; user: StaffUser }>("/api/auth/me");
    return r.user;
  },
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
};

/* ---------- dashboard ---------- */

export const dashboard = {
  stats: async () => {
    const r = await request<{ ok: true; stats: DashboardStats }>("/api/dashboard/stats");
    return r.stats;
  },
};

/* ---------- clients ---------- */

export const clients = {
  list: async (q?: { status?: string; search?: string; limit?: number; offset?: number; record_type?: "client" | "lead" | "all" }) => {
    // Backend's `search` param is named `q`; backend returns { ok, clients, count, limit, offset }
    const params: Record<string, string | number | undefined> = q ? { ...q } : {};
    if (q?.search !== undefined) { params.q = q.search; delete params.search; }
    const r = await request<{ ok: true; clients: ClientListItem[]; count: number; limit: number; offset: number }>(
      "/api/clients",
      { query: params as any },
    );
    return { items: r.clients ?? [], total: r.count ?? 0 };
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; client: Client } | Client>(`/api/clients/${id}`);
    return (r as any).client ?? r;
  },
  create: async (input: Partial<Client>) => {
    const r = await request<{ ok: true; client: Client }>("/api/clients", { method: "POST", body: input });
    return r.client;
  },
  update: async (id: string, patch: Partial<Client>) => {
    const r = await request<{ ok: true; client: Client } | Client>(`/api/clients/${id}`, { method: "PATCH", body: patch });
    return (r as any).client ?? r;
  },
  uploadIdentityDocument: async (id: string, kind: string, file: File) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    return request<{
      ok: true;
      document: { id: string; kind: string; file_url: string; uploaded_at: string };
      replaced: boolean;
      message: string;
    }>(`/api/clients/${id}/identity-documents`, { method: "POST", formData: fd });
  },
  importCsv: async (file: File) => {
    // Accept Excel (.xlsx/.xls) as well as CSV. Many dispute tools only export
    // Excel, so we convert the workbook's first sheet to CSV in the BROWSER and
    // feed the exact same tested CSV importer — no server changes, no new
    // Worker dependency. SheetJS is lazy-loaded so it stays out of the main bundle.
    let uploadFile = file;
    const isExcel = /\.xlsx?$/i.test(file.name)
      || /sheet|excel|spreadsheetml/i.test(file.type);
    if (isExcel) {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      if (!firstSheet) throw new Error("That spreadsheet has no sheets to read.");
      const csv = XLSX.utils.sheet_to_csv(firstSheet);
      uploadFile = new File([csv], file.name.replace(/\.xlsx?$/i, ".csv"), { type: "text/csv" });
    }
    const fd = new FormData();
    fd.append("file", uploadFile);
    return request<{ created: number; errors: Array<{ row: number; message: string }> }>(
      "/api/clients/import-csv",
      { method: "POST", formData: fd },
    );
  },
  /** Reveal the saved credit-monitoring login (audited server-side). */
  revealCfpb: async (id: string) => {
    const r = await request<{ ok: true; value: { email: string | null; password: string | null } | null }>(`/api/clients/${id}/reveal`, {
      method: "POST", body: { field: "cfpb_credentials" },
    });
    return r.value;
  },
  revealMonitoring: async (id: string) => {
    const r = await request<{ ok: true; value: { provider: string | null; username: string | null; password: string | null } | null }>(`/api/clients/${id}/reveal`, {
      method: "POST", body: { field: "monitoring_credentials" },
    });
    return r.value;
  },
  // ── Staff notes (running log per client) ──────────────────────────────
  listNotes: async (id: string) => {
    return request<{ ok: true; notes: ClientNote[]; can_write: boolean }>(`/api/clients/${id}/notes`);
  },
  addNote: async (id: string, body: string) => {
    const r = await request<{ ok: true; note: ClientNote }>(`/api/clients/${id}/notes`, { method: "POST", body: { body } });
    return r.note;
  },
  updateNote: async (id: string, note_id: string, body: string) => {
    const r = await request<{ ok: true; note: ClientNote }>(`/api/clients/${id}/notes`, { method: "PATCH", body: { note_id, body } });
    return r.note;
  },
  deleteNote: async (id: string, note_id: string) => {
    return request<{ ok: true; deleted: boolean }>(`/api/clients/${id}/notes`, { method: "DELETE", body: { note_id, confirm: true } });
  },
  // ── Messages (staff ↔ client thread; the client side lives in the portal) ──
  listMessages: async (id: string) => {
    return request<{ ok: true; messages: ClientMessage[]; can_write: boolean; unread: number }>(`/api/clients/${id}/messages`);
  },
  sendMessage: async (id: string, body: string) => {
    return request<{ ok: true; message: ClientMessage; email_delivered: boolean }>(`/api/clients/${id}/messages`, { method: "POST", body: { body } });
  },
  deleteMessage: async (id: string, message_id: string) => {
    return request<{ ok: true; deleted: boolean }>(`/api/clients/${id}/messages`, { method: "DELETE", body: { message_id, confirm: true } });
  },
  // PII reveal — logs an event server-side
  revealPii: (id: string, field: "ssn" | "dob") =>
    request<{ value: string }>(`/api/clients/${id}/reveal`, {
      method: "POST",
      body: { field },
    }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/clients/${id}`, { method: "DELETE" }),

  // One-click: analyze latest report -> auto-pick high/critical findings ->
  // generate dispute round -> render the 3 bureau bundle PDFs -> zip.
  //
  // Returns a Blob (the zip file) on success. CROA-blocked / no-parsed-report
  // failures surface as ApiError with the structured server code (e.g.
  // 'service_agreement_unsigned', 'no_parsed_report').
  // Send the latest credit analysis to the client (or a staff-chosen test
  // address). Pure staff→client comms; nothing is mailed to the bureaus.
  // 503 → { error: 'resend_not_configured', message } when Resend is missing.
  sendAnalysis: async (
    id: string,
    body: {
      confirm: true;
      recipient_email: string;
      custom_message?: string;
      include_finding_details?: boolean;
      send_mode?: string;
    },
  ) => {
    return await request<{ ok: true; message_id: string; recipient_email: string }>(
      `/api/clients/${id}/send-analysis`,
      { method: "POST", body },
    );
  },

  // ── Service Agreement (CROA-gated) ───────────────────────────────────
  // Staff endpoints for sending / revoking a service agreement and reading
  // the current state. The portal sign flow lives in the `portal` namespace.
  // ── Ink signature on file (drawn once, printed on every letter) ──────────
  getSignature: async (id: string) => {
    const r = await request<{ ok: true; signature: SignatureMeta | null }>(`/api/clients/${id}/signature`);
    return r.signature;
  },
  /** Authenticated PNG fetch → object URL (caller revokes). null when none. */
  getSignatureImageUrl: async (id: string): Promise<string | null> => {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(buildUrl(`/api/clients/${id}/signature/image`).toString(), { headers, credentials: "include", cache: "no-store" });
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, res.statusText, null);
    return URL.createObjectURL(await res.blob());
  },
  captureSignature: async (id: string, signature_png: string) => {
    const r = await request<{ ok: true; signature: SignatureMeta }>(`/api/clients/${id}/signature`, { method: "POST", body: { signature_png } });
    return r.signature;
  },
  revokeSignature: async (id: string) => {
    return request<{ ok: true; revoked: boolean }>(`/api/clients/${id}/signature`, { method: "DELETE", body: { confirm: true } });
  },
  sendServiceAgreement: async (
    id: string,
    body: {
      confirm: true;
      recipient_email?: string;
      total_cost: number;
      services_description?: string;
      test_mode?: boolean;
    },
  ) => {
    return await request<{
      ok: true;
      agreement_id: string;
      sign_url: string;
      recipient_email: string;
      requested_recipient: string;
      test_mode: boolean;
      sent_at: string;
      sign_token_expires_at: string;
      template_version: string;
      message_id: string;
    }>(`/api/clients/${id}/service-agreement/send`, { method: "POST", body });
  },
  revokeServiceAgreement: async (id: string) => {
    return await request<{
      ok: true;
      revoked_count: number;
      revoked: Array<{ id: string; sent_at: string | null; sent_to_email: string | null }>;
    }>(`/api/clients/${id}/service-agreement/revoke`, {
      method: "POST",
      body: { confirm: true },
    });
  },

  runFullDispute: async (
    id: string,
    opts?: { severity_min?: "high" | "critical" },
  ): Promise<{ blob: Blob; filename: string; headers: Headers }> => {
    const url = buildUrl(`/api/clients/${id}/run-full-dispute`);
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method: "POST",
        headers,
        body: JSON.stringify({ confirm: true, severity_min: opts?.severity_min || "high" }),
        credentials: "include",
      });
    } catch (e: any) {
      throw new ApiError(0, e?.message || "Network error", null);
    }
    if (!res.ok) {
      let body: any = null;
      const text = await res.text();
      if (text) { try { body = JSON.parse(text); } catch { body = text; } }
      const msg = (body && (body.message || body.error)) || res.statusText;
      throw new ApiError(res.status, msg, body);
    }
    const blob = await res.blob();
    const disp = res.headers.get("content-disposition") || "";
    const fnMatch = disp.match(/filename="?([^";]+)"?/i);
    const filename = fnMatch?.[1] || `full-dispute-${id.slice(0, 8)}.zip`;
    return { blob, filename, headers: res.headers };
  },
};

/* ---------- intake invitations (staff side) ---------- */

export const intakeInvitations = {
  send: async (clientId: string, channel: "sms" | "email" | "manual_link", deliveredTo: string) => {
    const r = await request<{ ok: true; invitation: IntakeInvitation }>("/api/intake-invitations", {
      method: "POST",
      body: { client_id: clientId, delivery_channel: channel, delivered_to: deliveredTo },
    });
    return r.invitation;
  },
  listForClient: async (clientId: string) => {
    const r = await request<{ ok: true; invitations: IntakeInvitation[]; count: number; limit: number; offset: number }>(
      "/api/intake-invitations",
      { query: { client_id: clientId } },
    );
    return r.invitations ?? [];
  },
};

/* ---------- public intake (uses token, not auth) ---------- */

export interface PortalInvoiceView {
  ok: true;
  /** True when a charge already went through, even if bookkeeping lagged. */
  payment_received?: boolean;
  company?: { name: string | null } | null;
  invoice: {
    id: string; client_name: string | null; client_email: string | null;
    status: "draft" | "sent" | "paid" | "void" | "refunded" | string;
    total_cents: number; currency: string; description: string | null;
    line_items: Array<{ description: string; amount_cents: number }> | null;
    created_at: string | null; sent_at: string | null; due_at: string | null;
  };
  processor: {
    id: string; name: string; supports_hosted_checkout: boolean; supports_direct_charge: boolean;
    hosted_checkout_url: string | null;
  } | null;
  acceptjs: { api_login_id: string; client_key: string; mode: string } | null;
  setup_problem: string | null;
}

export interface CompanyBranding {
  business_name: string;
  legal_entity?: string;
  logo_url?: string | null;
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  /** Where this company's clients send a 3-day cancellation notice. */
  cancel_email?: string | null;
  tagline?: string;
  /** Affiliate enrollment links shown on the intake monitoring step. */
  mfsn_affiliate_url?: string | null;
  smartcredit_affiliate_url?: string | null;
}

export interface IntakeContext {
  invitation: IntakeInvitation;
  client: {
    id: string;
    legal_name: string;
    email: string;
    phone: string;
  };
  /** White-label branding for the company running this intake (token → tenant). */
  company: CompanyBranding;
  /** Autosaved / seeded form state (e.g. monitoring_provider chosen on the sign-up link). */
  draft: Record<string, any>;
  /** A monitoring login is already saved for this client — skip that step. */
  monitoring_on_file?: boolean;
  steps_complete: {
    identity: boolean;
    agreements: boolean;
    documents: boolean;
    monitoring: boolean;
  };
}

// In-memory state for an in-progress intake. The form holds onto these
// between steps so the final submit can ship the whole bundle.
const _intakeBundles: Record<string, {
  identity?: any;
  agreements?: any;
  monitoring?: any;
  documents: Record<string, string>;
}> = {};

function _bundle(token: string) {
  if (!_intakeBundles[token]) _intakeBundles[token] = { documents: {} };
  return _intakeBundles[token];
}

export const intake = {
  // GET /api/intake/:token — backend returns { ok, intake: { token, client, draft, expires_at } }.
  // Unwrap and synthesize the IntakeContext shape the page reads.
  get: async (token: string): Promise<IntakeContext> => {
    const r = await request<{ ok: true; intake: any }>(`/api/intake/${token}`, { publicRoute: true });
    const i = r.intake ?? {};
    return {
      invitation: {
        id: i.id ?? "",
        client_id: i.client_id ?? "",
        token: i.token ?? token,
        delivery_channel: "manual_link",
        delivered_to: "",
        expires_at: i.expires_at ?? "",
        started_at: i.started_at ?? null,
        completed_at: i.completed_at ?? null,
        created_at: i.created_at ?? "",
      },
      client: {
        id: i.client?.id ?? "",
        legal_name: i.client?.legal_name ?? "",
        email: i.client?.email ?? "",
        phone: i.client?.phone ?? "",
      },
      draft: (i.draft && typeof i.draft === 'object') ? i.draft : {},
      monitoring_on_file: i.monitoring_on_file === true,
      company: {
        business_name: i.company?.business_name || "No Excuses Disputes",
        legal_entity: i.company?.legal_entity || i.company?.business_name || "Low Key Global Financial LLC",
        logo_url: i.company?.logo_url ?? null,
        address_line1: i.company?.address_line1 ?? null,
        address_city: i.company?.address_city ?? null,
        address_state: i.company?.address_state ?? null,
        address_zip: i.company?.address_zip ?? null,
        mfsn_affiliate_url: i.company?.mfsn_affiliate_url ?? null,
        smartcredit_affiliate_url: i.company?.smartcredit_affiliate_url ?? null,
        cancel_email: i.company?.cancel_email ?? null,
      },
      // "Done" means the data is IN THIS SESSION'S bundle — that is what the
      // final submit ships. The server draft only holds non-secret prefill; a
      // fresh session (reload, new device) therefore restarts at identity and
      // re-collects the SSN, signatures and uploads instead of skipping steps it
      // could not resubmit (review finding N5, 2026-09-07).
      steps_complete: {
        identity:    !!_bundle(token).identity,
        agreements:  !!_bundle(token).agreements,
        documents:   Object.keys(_bundle(token).documents).length >= 3,
        monitoring:  !!_bundle(token).monitoring,
      },
    };
  },

  // Each step autosaves to the SINGLE backend endpoint POST /api/intake/:token
  // (the per-step routes /identity, /agreements, /monitoring, /documents do
  // NOT exist on the backend — the backend uses one autosave endpoint that
  // takes any of the allowed keys). The form also remembers its state in
  // a module-local bundle so submit() can ship everything at once.
  saveIdentity: async (token: string, identity: {
    legal_name: string; dob: string; ssn_full: string;
    address_line1: string; address_city: string; address_state: string; address_zip: string;
  }) => {
    _bundle(token).identity = identity;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      // Don't autosave the raw SSN — keep it in client memory until final submit.
      body: { identity: { ...identity, ssn_full: undefined } },
      publicRoute: true,
    });
  },

  saveAgreements: async (token: string, agreements: {
    croa_disclosure_signed: boolean; service_agreement_signed: boolean; right_to_cancel_acknowledged: boolean;
    typed_name: string; attestation_text: string;
    /** Drawn signature PNG data URL — kept in memory, shipped only in the final submit. */
    signature_png?: string | null;
  }) => {
    _bundle(token).agreements = agreements;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      body: { agreements: { typed_name: agreements.typed_name, attestation_text: agreements.attestation_text,
                            croa_disclosure_signed: agreements.croa_disclosure_signed,
                            service_agreement_signed: agreements.service_agreement_signed,
                            right_to_cancel_acknowledged: agreements.right_to_cancel_acknowledged } },
      publicRoute: true,
    });
  },

  // Document upload — POSTs the file as multipart/form-data to the public
  // intake upload route; backend writes to Cloudflare R2 and returns the
  // canonical r2:// object URL that the final submit() ships in
  // identity_documents[].
  uploadDocument: async (
    token: string,
    kind: "government_id" | "ss_card" | "proof_of_address",
    file: File,
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const r = await request<{ ok: true; file_url: string; kind: string; bytes: number }>(
      `/api/intake/${token}/upload`,
      { method: "POST", formData: fd, publicRoute: true },
    );
    _bundle(token).documents[kind] = r.file_url;
    return r;
  },

  saveMonitoring: async (token: string, monitoring: {
    provider: "my_free_score_now" | "smart_credit"; username: string; password: string;
  }) => {
    _bundle(token).monitoring = monitoring;
    return request<{ ok: true }>(`/api/intake/${token}`, {
      method: "POST",
      // Don't autosave the monitoring password — keep in memory until submit.
      body: { monitoring_provider: monitoring.provider },
      publicRoute: true,
    });
  },

  // Final submit ships the full bundle to the backend submit endpoint.
  // The backend validates + persists everything in one go (encrypts SSN
  // and monitoring creds server-side, inserts agreements with IP/UA).
  submit: async (token: string) => {
    const b = _bundle(token);
    const identityIn = b.identity || {};
    const agreementsIn = b.agreements || {};
    const monitoringIn = b.monitoring || {};
    const body = {
      identity: {
        legal_name: identityIn.legal_name,
        dob: identityIn.dob,
        ssn: identityIn.ssn_full,
        address_line1: identityIn.address_line1,
        address_city: identityIn.address_city,
        address_state: identityIn.address_state,
        address_zip: identityIn.address_zip,
      },
      agreements: {
        croa_disclosure:    { kind: "croa_disclosure",    signed: !!agreementsIn.croa_disclosure_signed,    signature_name: agreementsIn.typed_name, content_version: "v1" },
        service_agreement:  { kind: "service_agreement",  signed: !!agreementsIn.service_agreement_signed,  signature_name: agreementsIn.typed_name, content_version: "v1" },
        right_to_cancel:    { kind: "right_to_cancel",    signed: !!agreementsIn.right_to_cancel_acknowledged, signature_name: agreementsIn.typed_name, content_version: "v1" },
        ...(agreementsIn.signature_png ? { signature_png: agreementsIn.signature_png } : {}),
      },
      identity_documents: Object.entries(b.documents).map(([kind, file_url]) => ({ kind, file_url })),
      monitoring: monitoringIn.provider ? {
        provider: monitoringIn.provider,
        username: monitoringIn.username,
        password: monitoringIn.password,
      } : undefined,
    };
    const result = await request<{ ok: true; client_id: string }>(`/api/intake/${token}/submit`, {
      method: "POST",
      body,
      publicRoute: true,
    });
    delete _intakeBundles[token];
    return result;
  },
};

/* ---------- credit reports ---------- */

export const reports = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; reports: CreditReport[]; count: number; limit: number; offset: number }>(
      "/api/credit-reports",
      { query: { client_id: clientId } },
    );
    return r.reports ?? [];
  },
  // PDF upload is NOT wired up. This used to DISCARD the chosen file and POST
  // a placeholder row with file_url:'(pending)', which looked like a successful
  // upload but produced a report with no body — so every later /analyze on it
  // failed, and the row sat in the list forever. (11 such dead rows exist in
  // production.) Refuse loudly instead of manufacturing a fake report.
  //
  // The real fix is the browser-side pdf.js text extraction + a
  // /api/credit-reports/upload-pdf endpoint (neither exists in this repo yet;
  // pdf.js needs DOM globals the Workers runtime lacks, so extraction has to
  // happen in the browser). That is a build, not a patch — see the audit note.
  upload: async (_clientId: string, _file: File) => {
    throw new Error(
      "PDF upload isn't wired up yet — uploading here would create an empty report that can't be analyzed. " +
      "Use \"Import from credentials\" for MyFreeScoreNow / SmartCredit clients.",
    );
  },
  /** "Pull now": import a fresh report with the client's saved monitoring
   *  login and analyze it in the same call. */
  pullNow: async (clientId: string) => {
    return await request<{
      ok: true;
      report: CreditReport;
      analysis?: any;
      scores?: { equifax?: number; experian?: number; transunion?: number };
    }>("/api/credit-reports/import", {
      method: "POST",
      body: { client_id: clientId, analyze: true },
    });
  },
  /** Compare the newest report with the report the dispute round was drafted from. */
  comparison: async (clientId: string, roundId?: string) => {
    const q = roundId ? `?round_id=${encodeURIComponent(roundId)}` : "";
    return await request<ReportComparison>(`/api/clients/${encodeURIComponent(clientId)}/report-comparison${q}`);
  },
  importFromCredentials: async (clientId: string) => {
    // Returns the whole envelope so the UI can show scores when present.
    return await request<{
      ok: true;
      report: CreditReport;
      scores?: { equifax?: number; experian?: number; transunion?: number };
      captured_at?: string;
      final_url?: string;
    }>("/api/credit-reports/import", {
      method: "POST",
      body: { client_id: clientId },
    });
  },
  // Run the FCRA analyzer over the captured HTML for this report. Persists
  // analysis_json + analyzed_at server-side and echoes the analysis back.
  analyze: async (reportId: string) => {
    const r = await request<{ ok: true; analysis: any }>(
      `/api/credit-reports/${reportId}/analyze`,
      { method: "POST" },
    );
    return r.analysis;
  },
  // Read the previously-stored analysis. Throws 404 if not analyzed yet,
  // letting the caller distinguish "no analysis" from a real error.
  getAnalysis: async (reportId: string) => {
    const r = await request<{ ok: true; analysis: any }>(
      `/api/credit-reports/${reportId}/analysis`,
    );
    return r.analysis;
  },
  // Run the MFSN parser over the captured HTML (read-only — does not persist).
  // Returns the parsed-report shape so the UI can render a viewer modal.
  getParsed: async (reportId: string) => {
    return await request<{ ok: true; report: CreditReport; parsed: any }>(
      `/api/credit-reports/${reportId}/parsed`,
    );
  },
};

/* ---------- disputes ---------- */

export const disputes = {
  list: async (q?: { client_id?: string; status?: string }) => {
    const r = await request<{ ok: true; rounds: DisputeRound[]; count: number; limit: number; offset: number }>(
      "/api/dispute-rounds",
      { query: q },
    );
    return r.rounds ?? [];
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; round: DisputeRound }>(`/api/dispute-rounds/${id}`);
    return r.round;
  },
  create: async (clientId: string) => {
    const r = await request<{ ok: true; round: DisputeRound }>("/api/dispute-rounds", {
      method: "POST",
      body: { client_id: clientId },
    });
    return r.round;
  },
  advance: async (id: string, status: DisputeRound["status"], extra?: { confirm?: boolean; letters?: { id: string; revision: string }[] }) => {
    const r = await request<{ ok: true; round: DisputeRound }>(`/api/dispute-rounds/${id}`, {
      method: "PATCH",
      body: { status, ...(extra || {}) },
    });
    return r.round;
  },
  // Materialize a round + draft letters from a set of checked analyzer findings.
  // Backend groups findings by (suggested_letter, recipient_bureau) into one
  // letter per group.
  createFromAnalysis: async (input: {
    credit_report_id: string;
    finding_ids: string[];
    round_notes?: string;
  }) => {
    const r = await request<{ ok: true; round: any; letters: any[] }>(
      "/api/dispute-rounds/from-analysis",
      { method: "POST", body: input },
    );
    return r;
  },
};

/* ---------- letters ---------- */

export const letters = {
  list: async (q?: { client_id?: string; status?: string; round_id?: string; view?: "needs_action" }) => {
    const r = await request<{ ok: true; letters: Letter[]; count: number; limit: number; offset: number }>(
      "/api/letters",
      { query: q },
    );
    return r.letters ?? [];
  },
  markMailed: async (id: string, mailedOn: string, note: string | undefined, revision: string | undefined) => {
    const r = await request<{ ok: true; letter: Partial<Letter> }>(`/api/letters/${id}/mailed`, {
      method: "POST", body: { mailed_on: mailedOn, note: note || undefined, revision, confirm: true },
    });
    return r.letter;
  },
  unmarkMailed: async (id: string) => {
    const r = await request<{ ok: true; letter: Partial<Letter> }>(`/api/letters/${id}/mailed`, {
      method: "DELETE", body: { confirm: true },
    });
    return r.letter;
  },
  create: async (input: { client_id: string; kind: Letter["kind"]; body_text?: string; dispute_round_id?: string }) => {
    const r = await request<{ ok: true; letter: Letter }>("/api/letters", { method: "POST", body: input });
    return r.letter;
  },
  preview: async (id: string) => {
    const r = await request<{
      ok: true;
      pdf_url: string | null;
      body_text?: string;
      recipient_address?: string;
      recipient_bureau?: string;
      kind?: string;
      status?: string;
    }>(`/api/letters/${id}/preview`);
    return {
      pdf_url: r.pdf_url ?? null,
      body_text: r.body_text ?? "",
      recipient_address: r.recipient_address ?? "",
      recipient_bureau: r.recipient_bureau,
      kind: r.kind,
      status: r.status,
    };
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; letter: Letter & { body_text?: string; recipient_address?: string; quality_gate_notes?: string | null; approved_at?: string | null } }>(`/api/letters/${id}`);
    return r.letter;
  },
  patch: async (
    id: string,
    body: { status?: string; body_text?: string; recipient_address?: string; quality_gate_notes?: string },
  ) => {
    const r = await request<{ ok: true; letter: Letter & { body_text?: string; recipient_address?: string } }>(`/api/letters/${id}`, {
      method: "PATCH",
      body,
    });
    return r.letter;
  },
  /** Delete a letter that never went out; a queued/mailed letter is archived instead (server decides). */
  remove: async (id: string) => {
    return request<{ ok: true; deleted: boolean; archived: boolean; message?: string }>(`/api/letters/${id}`, {
      method: "DELETE",
      body: { confirm: true },
    });
  },
};

/* ---------- mail ---------- */

export interface MailListResponse {
  jobs: MailJob[];
  providers: MailProviderInfo[];
  count: number;
  limit: number;
  offset: number;
}

export const mail = {
  list: async (q?: {
    status?: string;
    provider?: string;
    client_id?: string;
    round_id?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<MailListResponse> => {
    const r = await request<{
      ok: true;
      jobs: MailJob[];
      providers?: MailProviderInfo[];
      count: number;
      limit: number;
      offset: number;
    }>("/api/mail-jobs", { query: q });
    return {
      jobs: r.jobs ?? [],
      providers: r.providers ?? [],
      count: r.count ?? 0,
      limit: r.limit ?? 50,
      offset: r.offset ?? 0,
    };
  },
  // Queue a single-letter mail job. Backend requires confirm:true on mutation.
  queue: async (
    letterId: string,
    mail_type: "first_class" | "certified",
    // Required on purpose: mailing costs money, so the caller must name the
    // carrier. There is no house default.
    provider: MailProviderId,
    revision?: string,
  ) => {
    const r = await request<{ ok: true; job: MailJob }>("/api/mail-jobs", {
      method: "POST",
      body: { letter_id: letterId, mail_type, provider, confirm: true, revision },
    });
    return r.job;
  },
  // Dispatch a queued job to its provider.
  send: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob; result?: any }>(
      `/api/mail-jobs/${id}/send`,
      { method: "POST", body: { confirm: true } },
    );
    return r.job;
  },
  // Cancel a queued job (no provider call).
  cancel: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob }>(
      `/api/mail-jobs/${id}/cancel`,
      { method: "POST", body: { confirm: true } },
    );
    return r.job;
  },
  // Poll the provider for a status update on a sent job.
  status: async (id: string) => {
    const r = await request<{ ok: true; job: MailJob; polled?: any }>(
      `/api/mail-jobs/${id}/status`,
    );
    return r.job;
  },
  // Bulk-dispatch multiple queued jobs in one call.
  bulkSend: async (ids: string[]) => {
    const r = await request<{
      ok: true;
      sent: number;
      failed: number;
      results: Array<{ id: string; ok: boolean; status?: string; code?: string; error?: string }>;
    }>("/api/mail-jobs/bulk-send", {
      method: "POST",
      body: { ids, confirm: true },
    });
    return r;
  },
  // Render + send a per-bureau bundle as a single mail-job.
  sendBundle: async (args: {
    round_id: string;
    bureau: "experian" | "transunion" | "equifax";
    provider: MailProviderId;
    mail_type: "first_class" | "certified";
  }) => {
    const r = await request<{
      ok: true;
      mail_job_id: string;
      provider: MailProviderId;
      provider_id: string;
      status: MailJobStatus;
      tracking_url?: string | null;
      expected_delivery?: string | null;
    }>("/api/mail-jobs/bundle", {
      method: "POST",
      body: { ...args, confirm: true },
    });
    return r;
  },
};

/* ---------- filings (FTC / CFPB) ---------- */

export const filings = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; filings: Filing[] }>(
      "/api/filings",
      { query: { client_id: clientId } },
    );
    return r.filings ?? [];
  },
  get: async (id: string) => {
    const r = await request<{ ok: true; filing: Filing }>(`/api/filings/${id}`);
    return r.filing;
  },
  // Auto-draft a CFPB complaint (or FTC affidavit) from the client's dispute record.
  prepare: async (clientId: string, kind: FilingKind = "cfpb_complaint", bureaus?: string[]) => {
    const r = await request<{ ok: true; filing: Filing; draft_content: CfpbDraftContent }>(
      "/api/filings/prepare",
      { method: "POST", body: { client_id: clientId, kind, bureaus } },
    );
    return r;
  },
  patch: async (
    id: string,
    body: { narrative?: string; resolution?: string; bureaus?: string[]; publish_publicly?: boolean; draft_status?: FilingDraftStatus;
      /** Collector complaints only: the text and answers are computed from the client's file. */
      issue?: string; sub_issue?: string; refresh_facts?: boolean },
  ) => {
    const r = await request<{ ok: true; filing: Filing }>(`/api/filings/${id}`, { method: "PATCH", body });
    return r.filing;
  },
  // Records that a human pressed Submit at the agency portal (never auto-files).
  submit: async (id: string, body: { confirmation_number?: string; operator_assisted?: boolean }) => {
    const r = await request<{ ok: true; filing: Filing }>(`/api/filings/${id}/submit`, { method: "POST", body });
    return r.filing;
  },
  generateLink: async (id: string, delivery_channel: "manual_link" | "email" | "sms" = "manual_link", delivered_to?: string) => {
    const r = await request<{ ok: true; link: { token: string; public_url: string; expires_at: string } }>(
      `/api/filings/${id}/generate-link`,
      { method: "POST", body: { delivery_channel, delivered_to } },
    );
    return r.link;
  },
  // FTC IdentityTheft.gov — server-driven. Start sends the phone OTP; verify
  // relays the code and finalizes the report + stores the PDF.
  ftcStart: async (
    id: string,
    body: { attestation_confirmed: boolean; items?: FtcItemDraft[]; phone_override?: string },
  ) => {
    return await request<{ ok: true; awaiting_code: boolean; phone_last4: string; message: string; log?: string[] }>(
      `/api/filings/${id}/ftc/start`,
      { method: "POST", body },
    );
  },
  ftcVerify: async (id: string, code: string) => {
    return await request<{ ok: true; reference_number: string | null; report_path: string; log?: string[] }>(
      `/api/filings/${id}/ftc/verify`,
      { method: "POST", body: { code } },
    );
  },
};

export interface FtcItemDraft {
  company: string;
  noticed_mm?: string;
  noticed_yy?: string;
  opened_mm?: string;
  opened_yy?: string;
  amount?: string;
  account_number?: string;
}

/* ---------- compliance ---------- */

export const compliance = {
  list: async (clientId?: string) => {
    const r = await request<{ ok: true; docs: ComplianceDoc[]; count: number; limit: number; offset: number }>(
      "/api/compliance-docs",
      { query: { client_id: clientId } },
    );
    return r.docs ?? [];
  },
};

/* ---------- client portal ---------- */

export interface PortalClient {
  id: string;
  legal_name: string;
  email: string;
  /** Staff→client messages the client has not opened yet. */
  unread_messages?: number;
}

export interface PortalProgress {
  client?: PortalClient;
  score_start?: number | null;
  score_current?: number | null;
  rounds?: Array<{
    id: string;
    round_number: number;
    status: string;
    bureau: string;
    items_count: number;
    created_at: string;
    due_date?: string | null;
  }>;
  letters?: Array<{
    id: string;
    kind: string;
    bureau?: string | null;
    recipient?: string | null;
    status: string;
    created_at: string;
  }>;
  responses?: Array<{
    id: string;
    uploaded_at: string;
    original_filename: string;
    source_hint?: string | null;
    analyzed: boolean;
  }>;
}

export interface CorrespondenceUpload {
  id: string;
  client_id: string;
  original_filename: string;
  content_type: string;
  bytes: number;
  uploaded_at: string;
  uploaded_via?: string;
  client_note?: string | null;
  source_hint?: string | null;
  analyzed?: boolean;
  analysis_id?: string | null;
}

export interface CorrespondenceAnalysis {
  id?: string;
  correspondence_id?: string;
  analyzed_at?: string;
  ai_model?: string;
  bureau?: string | null;
  sender?: string | null;
  response_classification?: string | null;
  items_mentioned?: Array<{ creditor?: string; account_last4?: string | null; status?: string | null }> | null;
  recommended_action?: string | null;
  confidence?: string | null;
  summary?: string | null;
  raw_response?: string | null;
}

export interface PortalAgreement {
  ok: true;
  agreement_id: string;
  client_name: string;
  template_version: string;
  agreement_html: string;
  status: "pending" | "signed" | "expired" | "revoked";
  sent_at: string | null;
  sent_to_email: string | null;
  expires_at: string | null;
  signed_at: string | null;
  signed_by_name: string | null;
}

export const portal = {
  // Client-facing company branding for the portal chrome. Public — returns
  // the company's white-label brand when a session exists, else No Excuses.
  branding: async (): Promise<CompanyBranding> => {
    const r = await request<{ ok: true; company: CompanyBranding }>("/api/portal/branding", {
      publicRoute: true,
    });
    return r.company;
  },
  requestMagicLink: async (email: string) => {
    return request<{ ok: true }>("/api/portal/request-magic-link", {
      method: "POST",
      body: { email },
      publicRoute: true,
    });
  },
  login: async (email: string, password: string) => {
    return request<{ ok: true }>("/api/portal/login", {
      method: "POST",
      body: { email, password },
      publicRoute: true,
    });
  },
  setPassword: async (token: string, password: string) => {
    return request<{ ok: true }>("/api/portal/set-password", {
      method: "POST",
      body: { token, password },
      publicRoute: true,
    });
  },
  requestPasswordSetup: async (email: string) => {
    return request<{ ok: true }>("/api/portal/request-password-setup", {
      method: "POST",
      body: { email },
      publicRoute: true,
    });
  },
  me: async () => {
    const r = await request<{ ok: true; client: PortalClient }>("/api/portal/me");
    return r.client;
  },
  logout: () => request<{ ok: true }>("/api/portal/logout", { method: "POST" }),
  getProgress: async () => {
    // The endpoint wraps the payload as { ok, progress: {...} } — unwrap it so
    // callers get PortalProgress directly. (Reading it flat left score_start /
    // score_current undefined and silently defaulted rounds/letters to empty.)
    const r = await request<{ ok: true; progress: PortalProgress }>("/api/portal/progress");
    return r.progress;
  },
  listMessages: async () => {
    return request<{ ok: true; messages: ClientMessage[]; unread: number; company_name: string }>("/api/portal/messages");
  },
  sendMessage: async (body: string) => {
    return request<{ ok: true; message: ClientMessage }>("/api/portal/messages", { method: "POST", body: { body } });
  },
  listCorrespondence: async () => {
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload[]; count: number }>(
      "/api/portal/correspondence",
    );
    return r.correspondence ?? [];
  },
  // Invoice pay page — the emailed pay token IS the auth.
  getInvoice: async (token: string) => {
    return await request<PortalInvoiceView>(`/api/portal/invoice`, { query: { token }, publicRoute: true });
  },
  chargeInvoice: async (token: string, opaque_data: { dataDescriptor: string; dataValue: string }) => {
    return await request<{
      ok: true; invoice_id: string; payment_id: string; processor: string;
      processor_charge_id: string; amount_cents: number; currency: string; paid_at: string;
    }>(`/api/portal/invoice/charge`, { method: "POST", body: { token, opaque_data }, publicRoute: true });
  },
  // Service-agreement sign flow — token IS the auth, no session cookie needed.
  getAgreement: async (token: string) => {
    return await request<PortalAgreement>(`/api/portal/agreement`, {
      query: { token },
      publicRoute: true,
    });
  },
  signAgreement: async (token: string, typed_name: string, agree_checkbox: true, signature_png?: string | null) => {
    return await request<{
      ok: true;
      agreement_id: string;
      signed_at: string;
      confirmation_message_id: string | null;
      staff_notification_message_id: string | null;
    }>(`/api/portal/agreement/sign`, {
      method: "POST",
      body: { token, typed_name, agree_checkbox, ...(signature_png ? { signature_png } : {}) },
      publicRoute: true,
    });
  },

  uploadCorrespondence: async (file: File, sourceHint?: string, note?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (sourceHint) fd.append("source_hint", sourceHint);
    if (note) fd.append("client_note", note);
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload }>(
      "/api/portal/correspondence",
      { method: "POST", formData: fd },
    );
    return r.correspondence;
  },
};

/* ---------- correspondence (staff) ---------- */

export const correspondence = {
  listForClient: async (clientId: string) => {
    const r = await request<{ ok: true; correspondence: CorrespondenceUpload[]; count: number }>(
      "/api/correspondence",
      { query: { client_id: clientId } },
    );
    return r.correspondence ?? [];
  },
  analyze: async (id: string) => {
    const r = await request<{ ok: true; analysis: CorrespondenceAnalysis }>(
      `/api/correspondence/${id}/analyze`,
      { method: "POST" },
    );
    return r.analysis;
  },
  getAnalysis: async (id: string) => {
    const r = await request<{ ok: true; analysis: CorrespondenceAnalysis }>(
      `/api/correspondence/${id}/analysis`,
    );
    return r.analysis;
  },
  fileUrl: (id: string) => `/api/correspondence/${id}/file`,
};

/* ---------- billing ---------- */

export type InvoiceStatus = "draft" | "sent" | "paid" | "void" | "refunded";

export interface CompletedWorkRef {
  kind: "dispute_round" | "letter" | "milestone";
  id?: string | null;
  label: string;
}

export interface InvoiceLineItem {
  description: string;
  amount_cents: number;
  completed_work_ref: CompletedWorkRef;
}

export interface Invoice {
  id: string;
  client_id: string;
  client_name?: string | null;
  client_email?: string | null;
  status: InvoiceStatus;
  total_cents: number;
  currency: string;
  description: string | null;
  line_items_json: InvoiceLineItem[];
  paid_via_processor: string | null;
  processor_charge_id: string | null;
  created_at: string;
  sent_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  refunded_at: string | null;
  due_at: string | null;
}

export interface ProcessorListItem {
  id: string;
  name: string;
  description: string;
  supports_hosted_checkout: boolean;
  supports_direct_charge: boolean;
  is_configured: boolean;
  has_db_credentials: boolean;
  is_active: boolean;
  credential_rows: Array<{
    label: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>;
}

export interface BillingSummary {
  invoice_count: number;
  draft_count: number;
  sent_count: number;
  paid_count: number;
  void_count: number;
  refunded_count: number;
  outstanding_cents: number;
  collected_cents: number;
  last_invoice_at: string | null;
}

export const billing = {
  listProcessors: async () => {
    const r = await request<{ ok: true; processors: ProcessorListItem[]; future_processors: string[] }>(
      "/api/billing/processors",
    );
    return { processors: r.processors ?? [], future_processors: r.future_processors ?? [] };
  },
  saveProcessorCredentials: async (
    processor: string,
    payload: Record<string, unknown>,
    opts?: { label?: string; activate?: boolean },
  ) => {
    return request<{
      ok: true;
      processor: string;
      credential: { id: string; label: string | null; is_active: boolean; created_at: string; updated_at: string };
    }>(`/api/billing/processors/${processor}/credentials`, {
      method: "POST",
      body: { confirm: true, payload, label: opts?.label, activate: opts?.activate ?? true },
    });
  },
  listInvoices: async (q?: { client_id?: string; status?: InvoiceStatus | "" }) => {
    const r = await request<{ ok: true; invoices: Invoice[]; count: number }>("/api/billing/invoices", {
      query: q as any,
    });
    return r.invoices ?? [];
  },
  createInvoice: async (input: {
    client_id: string;
    line_items: InvoiceLineItem[];
    description?: string;
    due_at?: string;
    currency?: string;
  }) => {
    const r = await request<{ ok: true; invoice: Invoice }>("/api/billing/invoices", {
      method: "POST",
      body: { confirm: true, ...input },
    });
    return r.invoice;
  },
  sendInvoice: async (id: string, opts?: { processor?: string; test_mode?: boolean }) => {
    return request<{
      ok: true;
      invoice_id: string;
      processor: string;
      pay_url: string;
      portal_return_url: string;
      recipient_email: string;
      test_mode: boolean;
      message_id: string | null;
    }>(`/api/billing/invoices/${id}/send`, {
      method: "POST",
      body: { confirm: true, ...(opts || {}) },
    });
  },
  voidInvoice: async (id: string, reason?: string) => {
    return request<{ ok: true; invoice_id: string; status: "void" }>(`/api/billing/invoices/${id}/void`, {
      method: "POST",
      body: { confirm: true, reason },
    });
  },
  refundInvoice: async (id: string) => {
    return request<{ ok: true; invoice_id: string; status: "refunded"; refund_charge_id: string }>(
      `/api/billing/invoices/${id}/refund`,
      { method: "POST", body: { confirm: true } },
    );
  },
  billingSummary: async (clientId: string) => {
    const r = await request<{ ok: true; client_id: string; summary: BillingSummary }>(
      `/api/clients/${clientId}/billing-summary`,
    );
    return r.summary;
  },
};

/* ---------- company (multi-tenant settings) ---------- */

export interface CompanyProfile {
  name: string;
  slug: string;
  tier: "owner" | "mentorship" | "developer";
  can_whitelabel_software: boolean;
  status: "active" | "suspended";
  subscription_status: "none" | "active" | "past_due" | "canceled";
}

export interface CompanySettings {
  business_name?: string | null;
  legal_entity?: string | null;
  logo_url?: string | null;
  from_email?: string | null;
  reply_to_email?: string | null;
  internal_notification_email?: string | null;
  support_email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  mfsn_affiliate_url?: string | null;
  smartcredit_affiliate_url?: string | null;
  // The company's general sign-up link (docs/INTAKE-LINK-SPEC.md). Read-only URL;
  // the two settings round-trip through CompanyUpdate.
  intake_link?: { url: string; enabled: boolean; default_record_type: "lead" | "client" | null } | null;
  comms_provider: "log" | "ghl" | "twilio";
  ghl_location_id?: string | null;
  twilio_from?: string | null;
  ghl_configured?: boolean;
  twilio_configured?: boolean;
  // Mail carriers — members mail on their OWN Lob/LetterStream accounts.
  lob_configured?: boolean;
  letterstream_configured?: boolean;
  /** A Discord channel webhook is saved (write-only; never returned). */
  discord_configured?: boolean;
}

export type CompanyUpdate = Partial<Omit<CompanySettings, "ghl_configured" | "twilio_configured" | "lob_configured" | "letterstream_configured" | "discord_configured" | "intake_link">> & {
  intake_link_enabled?: boolean;
  intake_default_record_type?: "lead" | "client";
  /** Write-only: a Discord webhook URL to save, or "" to remove it. */
  discord_webhook_url?: string;
  // Secrets are write-only — supplied as plaintext, encrypted server-side, never read back.
  ghl_api_key?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  lob_api_key?: string;
  letterstream_api_id?: string;
  letterstream_api_key?: string;
};

// ── Manage staff / admin ──────────────────────────────────────────────────
export interface StaffMember {
  id: string; email: string; name: string | null; role: string;
  created_at: string; last_login_at: string | null; disabled: boolean;
}
export interface StaffInviteRow {
  id: string; email: string; name: string | null; role: string;
  expires_at: string; created_at: string;
}

export const staff = {
  list: () => request<{
    ok: true; staff: StaffMember[]; pending_invites: StaffInviteRow[];
    cap: number; slots_used: number; slots_left: number;
  }>("/api/staff"),
  invite: (email: string, role: string, name?: string) => request<{
    ok: true; invite: StaffInviteRow; invite_url: string; email: { mode: string };
  }>("/api/staff", { method: "POST", body: { email, role, name } }),
  remove: (id: string, kind: "user" | "invite") =>
    request<{ ok: true }>(`/api/staff/${id}`, { method: "DELETE", body: { kind } }),
};

export const staffInviteApi = {
  validate: (token: string) => request<{
    ok: true; invite: { email: string; name: string | null; role: string; company_name: string; expires_at: string };
  }>(`/api/staff-invite/${token}`, { publicRoute: true }),
  accept: (token: string, password: string, name?: string) => request<{
    ok: true; user: { id: string; email: string; name: string; role: string };
  }>(`/api/staff-invite/${token}/accept`, { method: "POST", body: { password, name }, publicRoute: true }),
};

export const staffAuth = {
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: true }>("/api/auth/change-password", { method: "POST", body: { current_password, new_password } }),
  forgotPassword: (email: string) =>
    request<{ ok: true; message: string }>("/api/auth/forgot-password", { method: "POST", body: { email }, publicRoute: true }),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true; message: string }>("/api/auth/forgot-password/reset", { method: "POST", body: { token, password }, publicRoute: true }),
};

export const company = {
  get: () => request<{ ok: true; company: CompanyProfile; settings: CompanySettings; intake_link?: CompanySettings["intake_link"] }>("/api/company"),
  update: (patch: CompanyUpdate) => request<{ ok: true }>("/api/company", { method: "PUT", body: patch }),
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ ok: true; logo_url: string }>("/api/company/logo", { method: "POST", formData: fd });
  },
};

/* ---------- internal notifications (bell) ---------- */

export interface InternalNotification {
  id: string;
  kind: string; // 'birthday' | ...
  client_id?: string | null;
  client_name?: string | null;
  title: string;
  body?: string | null;
  status: "unread" | "read" | "actioned" | "dismissed";
  due_date?: string | null;
  created_at: string;
}

export const notifications = {
  list: () => request<{ ok: true; notifications: InternalNotification[]; unread: number }>("/api/notifications"),
  setStatus: (id: string, status: "read" | "dismissed") =>
    request<{ ok: true }>(`/api/notifications/${id}`, { method: "PATCH", body: { status } }),
  // Birthday action: send a happy-birthday message, then mark the notification actioned.
  send: (id: string, input: { channel: "email" | "sms"; template?: string; custom_message?: string }) =>
    request<{ ok: true; sent: boolean }>(`/api/notifications/${id}/send`, { method: "POST", body: input }),
};

/* ---------- platform admin (super-admin only) ---------- */

export interface AdminCompany {
  id: string;
  name: string;
  slug: string;
  tier: string;
  status: "active" | "suspended";
  access_type: string;
  subscription_status: string;
  client_count: number;
  created_at: string;
}

export interface CompanyInvite {
  id: string;
  token: string;
  tier: "mentorship" | "developer";
  email?: string | null;
  note?: string | null;
  expires_at: string;
  used_at?: string | null;
  invite_url?: string;
  created_at: string;
}

export const admin = {
  listCompanies: () => request<{ ok: true; companies: AdminCompany[] }>("/api/admin/companies"),
  createCompany: (input: { company_name: string; admin_email: string; admin_name: string; admin_password: string; tier?: string }) =>
    request<{ ok: true; company: any; admin: any }>("/api/admin/companies", { method: "POST", body: input }),
  setCompanyStatus: (id: string, status: "active" | "suspended") =>
    request<{ ok: true }>(`/api/admin/companies/${id}`, { method: "PATCH", body: { status } }),
  listInvites: () => request<{ ok: true; invites: CompanyInvite[] }>("/api/admin/invites"),
  createInvite: (input: { tier?: "mentorship" | "developer"; email?: string; note?: string }) =>
    request<{ ok: true; invite: CompanyInvite }>("/api/admin/invites", { method: "POST", body: input }),
};

/* ---------- public invite acceptance ---------- */

export const invite = {
  validate: (token: string) =>
    request<{ ok: true; invite: { tier: string; email?: string | null; note?: string | null } }>(`/api/invite/${token}`, { publicRoute: true }),
  accept: (token: string, input: { company_name: string; admin_name: string; admin_email: string; admin_password: string }) =>
    request<{ ok: true; company: any; admin: any }>(`/api/invite/${token}/accept`, { method: "POST", body: input, publicRoute: true }),
};

export const api = {
  auth,
  dashboard,
  clients,
  intakeInvitations,
  intake,
  reports,
  disputes,
  letters,
  mail,
  filings,
  compliance,
  portal,
  correspondence,
  billing,
  company,
  notifications,
  admin,
  invite,
};

// ── Public sign-up link (/start/:slug) ─────────────────────────────────────
// Contract: docs/INTAKE-LINK-SPEC.md. Public routes: no staff token is sent.
export interface StartPageInfo {
  ok: true;
  slug: string;
  company: { name: string | null; logo_url: string | null; support_email: string | null; phone: string | null };
  monitoring: { my_free_score_now: string | null; smart_credit: string | null };
  default_record_type: "lead" | "client" | null;
}
export interface StartSubmitBody {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  monitoring_choice: "my_free_score_now" | "smart_credit" | null;
  consent: true;
  source: string | null;
  referrer: string | null;
  /** honeypot — must stay empty */
  website: string;
}
export interface StartSubmitResult {
  ok: true;
  intake_url: string | null;
  monitoring_url: string | null;
  client_state: "created" | "existing";
  /** existing clients only: was the secure link actually emailed to the address on file? */
  delivery?: "sent" | "failed" | null;
}
export const startApi = {
  get: (slug: string) => request<StartPageInfo>(`/api/start/${encodeURIComponent(slug)}`, { publicRoute: true }),
  submit: (slug: string, body: StartSubmitBody) =>
    request<StartSubmitResult>(`/api/start/${encodeURIComponent(slug)}`, { method: "POST", body, publicRoute: true }),
};

/* ---------- public software sales (/pricing) ---------- */
export type SoftwareTier = "solo" | "pro" | "unlimited";
/** Business page tiers vs the separate consumer ("AI credit") funnel tiers. */
export type SoftwareOffer = "business" | "consumer";
export type ConsumerTier = "solo" | "family" | "unlimited";
export interface SoftwarePlan<T extends string = SoftwareTier> {
  tier: T;
  label: string;
  max_clients: number | null;
  amount_cents: number;
  currency: string;
  interval: string;
}
export interface SoftwareSalesConfig<T extends string = SoftwareTier> {
  ok: true;
  enabled: boolean;
  plans: SoftwarePlan<T>[];
}
export const softwareSales = {
  config: () => request<SoftwareSalesConfig>("/api/checkout/dispute-manager", { publicRoute: true }),
  consumerConfig: () =>
    request<SoftwareSalesConfig<ConsumerTier>>("/api/checkout/dispute-manager?offer=consumer", { publicRoute: true }),
  captureLead: (body: {
    full_name: string; business_name: string; email: string; phone: string; website?: string;
    tier?: SoftwareTier | ConsumerTier; offer?: SoftwareOffer; source?: string; utm?: Record<string, string> | null; referrer?: string | null;
  }) => request<{ ok: true; captured: boolean }>("/api/software-leads", { method: "POST", body, publicRoute: true }),
  startCheckout: (email: string, tier: SoftwareTier) =>
    request<{ ok: true; url: string }>("/api/checkout/dispute-manager", { method: "POST", body: { email, tier }, publicRoute: true }),
  /** Consumer funnel: the buyer must have ticked the do-it-yourself box (server-enforced). */
  startConsumerCheckout: (email: string, tier: ConsumerTier) =>
    request<{ ok: true; url: string }>("/api/checkout/dispute-manager", {
      method: "POST", body: { email, tier, offer: "consumer", acknowledged: true }, publicRoute: true,
    }),
};

/* ---------- report-to-report comparison (baseline = report the round was drafted from) ---------- */
export type ComparisonBureau = "equifax" | "experian" | "transunion";
export interface ComparisonAccount {
  creditor_name: string;
  account_number_last4: string | null;
  bureau: string;
  was_negative: boolean;
  is_negative: boolean | null;
  before_status: string | null;
  after_status: string | null;
  changes: string[];
}
export interface ComparisonInquiry { creditor_name: string; inquiry_date: string | null; bureau: string }
export interface ComparisonPublicRecord { kind: string; case_number: string | null; filing_date: string | null; bureau: string }
export interface ReportComparison {
  ok: true;
  reason: null | "no_round" | "no_baseline" | "no_newer_report" | "current_incomplete";
  /** Bureaus each report actually covers; removals are only claimed for bureaus the newest report covers. */
  coverage?: { baseline: string[]; current: string[] } | null;
  warnings?: string[];
  round: { id: string; round_number: number; created_at: string; mailed_at: string | null } | null;
  baseline: { report_id: string; imported_at: string } | null;
  current: { report_id: string; imported_at: string } | null;
  scores: Partial<Record<ComparisonBureau, { before: number | null; after: number | null; delta: number | null }>> | null;
  accounts: { removed: ComparisonAccount[]; updated: ComparisonAccount[]; still_reporting: ComparisonAccount[]; new_negative: ComparisonAccount[] };
  inquiries: { removed: ComparisonInquiry[]; still_reporting: ComparisonInquiry[]; new: ComparisonInquiry[] };
  public_records: { removed: ComparisonPublicRecord[]; still_reporting: ComparisonPublicRecord[]; new: ComparisonPublicRecord[] };
}

/* ---------- self-serve subscription (Settings → Subscription) ---------- */
export interface BillingSubscription {
  ok: true;
  has_subscription: boolean;
  /** true only for the company owner on a Stripe subscription */
  manageable: boolean;
  plan: { software_plan: string; label: string; max_clients: number | null } | null;
  status: string | null;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  cancel_at: string | null;
  /** The company is locked because its subscription ended. */
  suspended?: boolean;
  /** Owner can restart the same plan from the lock screen. */
  renewable?: boolean;
  /** Latest renewal attempt: pending = paid/processing, failed = payment didn't go through (retry allowed). */
  renewal?: { status: "none" | "pending" | "failed"; failed_at: string | null };
}
export const billingSubscription = {
  get: () => request<BillingSubscription>("/api/billing/subscription"),
  /** flow "cancel" deep-links to Stripe's cancel step (with the save offer when eligible). */
  openPortal: (flow?: "cancel") =>
    request<{ ok: true; url: string }>("/api/billing/portal", { method: "POST", body: flow ? { flow } : undefined }),
  renew: () => request<{ ok: true; url: string }>("/api/billing/renew", { method: "POST" }),
};

/* ---------- collection accounts: debt validation letter + CFPB complaint against the collector ---------- */
export interface CollectionAccount {
  finding_id: string;
  creditor_name: string;
  original_creditor: string | null;
  account_number_as_reported: string | null;
  bureaus: string[];
  balance_cents: number | null;
  status_text: string | null;
  validation_letter: { id: string; status: string; mailed_at: string | null } | null;
  response_logged: boolean;
  cfpb_filing: { id: string; draft_status: string; submitted_at: string | null } | null;
}
export interface CollectorComplaintDraft {
  agency: "cfpb";
  target: "collector";
  finding_id: string;
  wizard: {
    product: string; sub_product: string; issue: string; sub_issue: string;
    tried_to_fix: boolean; requested_information: boolean; company_provided_information: boolean | null;
    submitting_for: string; publish_publicly: boolean;
  };
  company: { name_as_reported: string; search_hint: string };
  account_number_as_reported: string | null;
  bureaus_reporting: string[];
  narrative: string;
  resolution: string;
  attachments: Array<{ filename: string; path: string }>;
  facts: { validation_letter_id: string | null; validation_mailed_at: string | null; days_since_mailed: number | null; response_logged: boolean };
  warnings: string[];
  blockers: string[];
  checklist: string[];
  client_identity?: { name_as_on_report?: string; ssn_last4_on_file?: boolean; dob_on_file?: boolean };
  cfpb_account_on_file?: boolean;
}
export const collections = {
  list: (clientId: string) =>
    request<{ ok: true; report_id: string | null; accounts: CollectionAccount[] }>(`/api/clients/${encodeURIComponent(clientId)}/collection-accounts`),
  draftValidationLetter: (clientId: string, findingId: string, body: { collector_name?: string; collector_address: string; round_id?: string }) =>
    request<{ ok: true; letter: { id: string; kind: string; status: string }; round: { id: string; round_number: number } }>(
      `/api/clients/${encodeURIComponent(clientId)}/collection-accounts/${encodeURIComponent(findingId)}/validation-letter`, { method: "POST", body }),
  prepareComplaint: (clientId: string, findingId: string, issue?: { issue: string; sub_issue: string }) =>
    request<{ ok: true; filing: Filing; draft_content: CollectorComplaintDraft }>("/api/filings/prepare", {
      method: "POST", body: { client_id: clientId, kind: "cfpb_collector_complaint", finding_id: findingId, ...(issue || {}) },
    }),
};
/** Plain-English text for the warnings the collector complaint can carry. */
export function collectorWarningText(code: string): string {
  if (code === "validation_letter_not_sent") return "No validation letter has been mailed to this collector yet, so the complaint will truthfully say you have not contacted them. It can still be filed, but sending the letter first makes it stronger.";
  if (code === "validation_under_30_days") return "The validation letter was mailed less than 30 days ago. The collector may still be within its time to answer.";
  return code.replace(/_/g, " ");
}
